/**
 * better-auth クライアントインスタンス
 * ブラウザ側で使用する認証クライアント
 */

import { createAuthClient } from 'better-auth/react'
import { twoFactorClient } from 'better-auth/client/plugins'
import { getSkewProtectionHeaders } from './skew-protection-headers'
import { buildTwoFactorPageHref } from '@/lib/utils/safe-next-path'

// AUTH-1（2026-08-30・owner裁定）: better-authのクライアントfetchはVercel Skew Protectionの
// 自動保護対象外（フレームワークが管理するページ遷移・静的アセットのみ自動ピン留めされる）。
// デプロイ直後に古いJSを保持したクライアントが最新デプロイのAPIへ到達し、
// クライアント/サーバー間のコード版ズレでログインが固着する事故の再発防止として、
// Vercel公式パターン（https://vercel.com/docs/skew-protection）通りx-deployment-idを付与する。
// VERCEL_SKEW_PROTECTION_ENABLED/VERCEL_DEPLOYMENT_ID はローカル開発では未設定（undefined）になり、
// その場合はヘッダーを付与しない（=既存挙動のまま・fail-closedではなく機能無効化のno-op）。
const client = createAuthClient({
  baseURL:
    typeof window !== 'undefined' ? window.location.origin : process.env.BETTER_AUTH_URL || '',
  plugins: [
    twoFactorClient({
      // 固定の '/two-factor'（twoFactorPage）だと、ログイン画面の ?next= が落ちて
      // 2FA を通った人は共有リンクの行き先を失う（2026-10-16・Codex SO）。行き先を引き継いで移る。
      onTwoFactorRedirect: () => {
        window.location.href = buildTwoFactorPageHref(window.location.search)
      },
    }),
  ],
  fetchOptions: {
    headers: getSkewProtectionHeaders(),
  },
})

// better-auth client uses Proxy - forgetPassword/resetPassword exist at runtime
// when emailAndPassword is enabled on the server, but TypeScript's type inference
// for the client doesn't include these methods without server-side type passing.
type AuthActionResult = Promise<{ data: unknown; error: { message: string } | null }>

type ForgetPasswordParams = { email: string; redirectTo?: string }

type AuthClient = typeof client & {
  forgetPassword: (params: ForgetPasswordParams) => AuthActionResult
  resetPassword: (params: { newPassword: string; token: string }) => AuthActionResult
}

/**
 * `forgetPassword` だけサーバの実パスへ差し替える。
 *
 * better-auth のクライアントは Proxy で、**メソッド名を kebab-case にしたパスをそのまま叩く**
 * (`node_modules/better-auth/dist/client/proxy.mjs:31`
 *  `const routePath = "/" + path.map(toKebabCase).join("/");`)。
 * つまり `forgetPassword` は `/api/auth/forget-password` を叩くが、**サーバ側にそのルートは無い**。
 * 実エンドポイントは `/request-password-reset`
 * (`node_modules/better-auth/dist/api/routes/password.mjs:21`) で、これは better-auth 1.6 系でも同じ。
 *
 * 2026-08-29 の本番実測（Vercel runtime logs・直近24h）で `/api/auth/forget-password` は
 * **7 件すべて 404**。全社ログイン障害の最中に、利用者がパスワード再設定で自力回復することも
 * できない状態になっていた。`lib/auth/auth.ts` の rateLimit 側は既に `/request-password-reset`
 * へ直っており（同ファイルに「旧 customRules['/forget-password'] は存在しないパスのため
 * rate limit 無効だった」というコメントが残っている）、**片側だけ直った状態が続いていた**。
 *
 * 呼び出し側（`ICpochipochi/.../UserManager.tsx`。`forgot-password/page.tsx`・`use-login-form.ts` は
 * ADR-055 で画面ごと消えた）を書き換えると追従漏れが起きるため、
 * メソッド名は変えずにここでパスだけを差し替える。リクエストボディの形は
 * `{ email, redirectTo? }` で `/request-password-reset` の body スキーマと同一なのでそのまま渡せる。
 *
 * 機構: `lib/auth/__tests__/auth-client-endpoint-paths.test.ts`（存在しないパスへ戻ると赤くなる）
 */
export const authClient = new Proxy(client, {
  get(target, prop, receiver) {
    if (prop === 'forgetPassword') {
      return (params: ForgetPasswordParams): AuthActionResult =>
        (
          target as unknown as {
            requestPasswordReset: (p: ForgetPasswordParams) => AuthActionResult
          }
        ).requestPasswordReset(params)
    }
    return Reflect.get(target, prop, receiver)
  },
}) as AuthClient

// BT 互換エクスポート: BT は `import { signIn, signOut, useSession } from '@/lib/auth/auth-client'` を使用
export const { signIn, signOut, signUp, useSession } = client
